/**
 * Localized names of the storefront's own routes (/koszyk → /cart, /warenkorb...). The app's routes
 * keep their Polish names; proxy.ts rewrites every alias onto them and links are built in the
 * visitor's language. Pure module — shared by proxy.ts, server and client code.
 */
const LOCALIZED_ROUTES: Record<string, Record<string, string>> = {
  en: {
    '/kampania': '/campaign',
    '/koszyk': '/cart',
    '/zamowienie': '/checkout',
    '/logowanie': '/login',
    '/rejestracja': '/register',
    '/moje-konto': '/my-account',
    '/produkty': '/products',
    '/kategoria': '/category',
  },
  de: {
    '/kampania': '/aktion',
    '/koszyk': '/warenkorb',
    '/zamowienie': '/kasse',
    '/logowanie': '/anmeldung',
    '/rejestracja': '/registrierung',
    '/moje-konto': '/mein-konto',
    '/produkty': '/produkte',
    '/kategoria': '/kategorie',
  },
  es: {
    '/kampania': '/campana',
    '/koszyk': '/carrito',
    '/zamowienie': '/pago',
    '/logowanie': '/iniciar-sesion',
    '/rejestracja': '/registro',
    '/moje-konto': '/mi-cuenta',
    '/produkty': '/productos',
    '/kategoria': '/categoria',
  },
  fr: {
    '/kampania': '/campagne',
    '/koszyk': '/panier',
    '/zamowienie': '/caisse',
    '/logowanie': '/connexion',
    '/rejestracja': '/inscription',
    '/moje-konto': '/mon-compte',
    '/produkty': '/produits',
    '/kategoria': '/categorie',
  },
  cs: {
    '/kampania': '/kampan',
    '/koszyk': '/kosik',
    '/zamowienie': '/pokladna',
    '/logowanie': '/prihlaseni',
    '/rejestracja': '/registrace',
    '/moje-konto': '/muj-ucet',
    '/produkty': '/produkty',
    '/kategoria': '/kategorie',
  },
  sk: {
    '/kampania': '/kampan',
    '/koszyk': '/kosik',
    '/zamowienie': '/pokladna',
    '/logowanie': '/prihlasenie',
    '/rejestracja': '/registracia',
    '/moje-konto': '/moj-ucet',
    '/produkty': '/produkty',
    '/kategoria': '/kategoria',
  },
  it: {
    '/kampania': '/campagna',
    '/koszyk': '/carrello',
    '/zamowienie': '/pagamento',
    '/logowanie': '/accedi',
    '/rejestracja': '/registrati',
    '/moje-konto': '/mio-account',
    '/produkty': '/prodotti',
    '/kategoria': '/categoria',
  },
  nl: {
    '/kampania': '/actie',
    '/koszyk': '/winkelwagen',
    '/zamowienie': '/afrekenen',
    '/logowanie': '/inloggen',
    '/rejestracja': '/registreren',
    '/moje-konto': '/mijn-account',
    '/produkty': '/producten',
    '/kategoria': '/categorie',
  },
};

// '/cart' → '/koszyk', '/warenkorb' → '/koszyk'... (every locale's alias back to the app route)
const ROUTE_ALIASES: Record<string, string> = Object.fromEntries(
  Object.values(LOCALIZED_ROUTES).flatMap((routes) =>
    Object.entries(routes)
      .filter(([internal, localized]) => internal !== localized)
      .map(([internal, localized]) => [localized, internal]),
  ),
);

/** The app route behind a localized alias (/en-style '/cart/x' → '/koszyk/x'), or null. */
export function toInternalRoute(path: string): string | null {
  if (ROUTE_ALIASES[path]) return ROUTE_ALIASES[path];
  for (const [alias, internal] of Object.entries(ROUTE_ALIASES)) {
    if (path.startsWith(`${alias}/`)) return internal + path.slice(alias.length);
  }
  return null;
}

/** Every visitor-facing first segment that maps onto `internalPrefix` (e.g. all names of /produkty). */
export function routeAliasesOf(internalPrefix: string): string[] {
  return Object.entries(ROUTE_ALIASES).filter(([, internal]) => internal === internalPrefix).map(([alias]) => alias);
}

/** An app path ('/koszyk', '/kategoria/x?y=1') with its route named in `locale`. */
export function localizeRoute(locale: string, path: string): string {
  const routes = LOCALIZED_ROUTES[locale];
  if (!routes || !path) return path;
  const match = path.match(/^([^?#]*)(.*)$/);
  const pathname = match?.[1] ?? path;
  const suffix = match?.[2] ?? '';
  const withSlash = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const restoreSlash = (value: string) => (pathname.startsWith('/') ? value : value.substring(1));
  if (routes[withSlash]) return restoreSlash(routes[withSlash]) + suffix;
  for (const [internal, localized] of Object.entries(routes)) {
    if (withSlash.startsWith(`${internal}/`)) return restoreSlash(localized + withSlash.slice(internal.length)) + suffix;
  }
  return path;
}
